import {
  useDashboardPageContext,
  useFormatZoned,
  useSelectedDay,
} from "utils/customHooks/genericHooks";
import { Button } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { goToDay } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import { getFullDayRangeTitle, ONE_DAY_OF_MS } from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { useDrag } from "react-use-gesture";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { HtmlTooltip } from "components/Dashboard/GroupMetrics/HtmlTooltip";
import { TimeOffsetExplainer } from "./TimeOffsetExplainer";
import { getIsSafari } from "constants/constants";
import { useIsTrendChartExpandedTimelinePage } from "pages/DashboardPages/dashboardPagesUtils";
import { startTransition } from "react";

export function FullDayButton() {
  const { translate } = useFlightTranslation();
  const selectedDay = useSelectedDay();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const { dayStartMsOffset } = useSettings();
  const formatZoned = useFormatZoned();
  const { isFullDaySelected, setIsFullDaySelected } = useDashboardPageContext();

  const [, setIsTrendChartExpanded] = useIsTrendChartExpandedTimelinePage();
  const dispatch = useDispatch();
  /** prevent mouseUp after dragging from triggering click */
  const bind = useDrag(({ tap }) => {
    if (tap && !isFullDaySelected) {
      // iOS Safari is significantly memory-throttled & crashes in 24hr mode due to TrendChart component being very expensive,
      // so, collapse TrendChart when picking 24hr mode

      const isSafari = getIsSafari();
      if (isSafari) {
        setIsTrendChartExpanded(false);
      }
      // give a bit of time for trend chart to collapse before actually changing the date
      startTransition(() => {
        setIsFullDaySelected(true);
        const fullDayName = getFullDayRangeTitle(
          selectedDay.range.ge + dayStartMsOffset,
          selectedDay.range.lt + dayStartMsOffset,
          formatZoned
        );
        dispatch(
          goToDay({
            newSelectedDay: {
              shift: { name: fullDayName },
              range: {
                ge: selectedDay.range.ge,
                lt: selectedDay.range.lt,
              },
            },
            dayStartMsOffset,
            timeZoneMsOffset,
            shouldGoToFirstShiftInDay: false,
            formatZoned,
          })
        );
      });
    }
  });
  const rangeStart = selectedDay.range.ge + dayStartMsOffset + timeZoneMsOffset;

  const shouldShowTooltip = dayStartMsOffset !== 0;

  return (
    <HtmlTooltip
      title={
        shouldShowTooltip ? (
          <TimeOffsetExplainer showTimeZoneInfo={false} />
        ) : (
          ""
        )
      }
      placement="bottom"
      openOnMouseover={true}
    >
      <Button
        disabled={isFullDaySelected}
        {...bind()}
        className={`btn24Hours ${isFullDaySelected ? " active" : ""}`}
        style={{ pointerEvents: "auto" }}
      >
        {shouldShowTooltip ? (
          <div className="tooltipIndicator">{"*"}</div>
        ) : null}
        <div className="rangeName" style={{ pointerEvents: "none" }}>
          {translate("24_Hours")}
        </div>
        <div className="range" style={{ pointerEvents: "none" }}>
          {formatZoned(rangeStart, "E HH:mm")} -{" "}
          {formatZoned(rangeStart + ONE_DAY_OF_MS, "E HH:mm")}
        </div>
      </Button>
    </HtmlTooltip>
  );
}
